/**
 * Species parameter bundles for the Ray II AOI, fed to the branching grammar
 * (treeSkeleton.ts). Three species matching the existing placement taxonomy in
 * vegetationPlacement.ts (AREA_PLANTING / treeRowInstances assign tree
 * `variant` 0/1/2):
 *
 *   variant 0 -> broadleaf  (alder/rowan-like round crown; wood/hedgerow)
 *   variant 1 -> conifer    (sitka spruce shelterbelt; dense forest plantation)
 *   variant 2 -> small tree (hawthorn; scrubby sapling)
 *
 * Each species is grown into STRUCTURAL_VARIANTS distinct skeletons (same
 * params, different seed stream) so no two neighbouring trees share a mesh
 * (issue #58 AC: "no two adjacent trees look identical"). Parameter shapes are
 * adapted from Braffolk/fable5-world-demo (MIT licence, commit fd75fdb).
 *
 * Pure data; no three.js. Values are tuned so a near-LOD mesh stays well under
 * the ~5k triangle budget (verified by treeGeometry.test.ts).
 */
import type { SpeciesParams } from './treeTypes.ts';

/** Structural variants grown per species (distinct skeletons, shared params). */
export const STRUCTURAL_VARIANTS = 2;

/** Placement `variant` (0/1/2) -> species index. Kept 1:1 for clarity. */
export const SPECIES_FOR_PLACEMENT_VARIANT = [0, 1, 2] as const;

export const TREE_SPECIES: SpeciesParams[] = [
  // 0 — broadleaf (alder / rowan): round ellipsoid crown, spreading primaries.
  {
    id: 'broadleaf',
    label: 'Alder / rowan (broadleaf)',
    kind: 'broadleaf',
    height: [7, 13],
    trunkRadiusK: 0.03,
    crown: 'ellipsoid',
    asym: 0.28,
    barkRepeats: 4,
    atlasColumn: 0,
    flare: { amp: 0.5, height: 1.1, lobes: 5 },
    levels: [
      // trunk
      {
        density: 0, whorl: 0, childStart: 0, childEnd: 0, angleBase: 0, angleTip: 0,
        lenRatio: 0, lenJitter: 0, radRatio: 0, segs: 7, wander: 0.05,
        gravitropism: 0.02, droop: 0.0, tipCurl: 0.0, taper: 1.15,
      },
      // primaries
      {
        density: 0.55, whorl: 0, childStart: 0.32, childEnd: 0.95,
        angleBase: 1.15, angleTip: 0.7, lenRatio: 0.62, lenJitter: 0.28,
        radRatio: 0.52, segs: 5, wander: 0.16, gravitropism: 0.06,
        droop: 0.35, tipCurl: 0.18, taper: 1.2,
      },
      // secondaries (carry foliage)
      {
        density: 0.9, whorl: 0, childStart: 0.28, childEnd: 0.98,
        angleBase: 1.0, angleTip: 0.6, lenRatio: 0.5, lenJitter: 0.35,
        radRatio: 0.5, segs: 4, wander: 0.28, gravitropism: 0.04,
        droop: 0.3, tipCurl: 0.22, taper: 1.3, planar: 0.25,
      },
    ],
    foliage: {
      anchorLevel: 2, spacing: 0.55, tStart: 0.18, scale: [0.7, 1.15],
      tilt: 0.7, normalBend: 0.6, cardMode: 'cross', sizeK: 1.5,
    },
  },
  // 1 — conifer (sitka spruce): conical crown, whorled branches, tip curl.
  {
    id: 'conifer',
    label: 'Sitka spruce (conifer)',
    kind: 'conifer',
    height: [9, 17],
    trunkRadiusK: 0.026,
    crown: 'cone',
    asym: 0.12,
    barkRepeats: 5,
    atlasColumn: 1,
    flare: { amp: 0.42, height: 1.0, lobes: 6 },
    levels: [
      {
        density: 0, whorl: 0, childStart: 0, childEnd: 0, angleBase: 0, angleTip: 0,
        lenRatio: 0, lenJitter: 0, radRatio: 0, segs: 8, wander: 0.03,
        gravitropism: 0.03, droop: 0.0, tipCurl: 0.0, taper: 1.4,
      },
      // whorled primaries: rings of 5 branches, near-horizontal, drooping
      {
        density: 0.7, whorl: 5, childStart: 0.14, childEnd: 0.97,
        angleBase: 1.4, angleTip: 1.05, lenRatio: 0.5, lenJitter: 0.2,
        radRatio: 0.42, segs: 5, wander: 0.1, gravitropism: -0.02,
        droop: 0.5, tipCurl: 0.35, taper: 1.25,
      },
      // secondaries (carry needle-spray cards), two-sided along the bough
      {
        density: 1.0, whorl: 0, childStart: 0.15, childEnd: 0.98,
        angleBase: 0.95, angleTip: 0.7, lenRatio: 0.42, lenJitter: 0.25,
        radRatio: 0.5, segs: 3, wander: 0.16, gravitropism: 0.0,
        droop: 0.35, tipCurl: 0.4, taper: 1.3, planar: 0.85,
      },
    ],
    foliage: {
      anchorLevel: 2, spacing: 0.4, tStart: 0.1, scale: [0.55, 0.9],
      tilt: 0.5, normalBend: 0.55, planarLeaves: true, cardMode: 'lying', sizeK: 1.35,
    },
  },
  // 2 — small tree (hawthorn): low, irregular dome, twiggy.
  {
    id: 'hawthorn',
    label: 'Hawthorn (small tree)',
    kind: 'broadleaf',
    height: [3.5, 6.5],
    trunkRadiusK: 0.034,
    crown: 'dome',
    asym: 0.38,
    barkRepeats: 3,
    atlasColumn: 2,
    flare: { amp: 0.55, height: 0.7, lobes: 4 },
    levels: [
      {
        density: 0, whorl: 0, childStart: 0, childEnd: 0, angleBase: 0, angleTip: 0,
        lenRatio: 0, lenJitter: 0, radRatio: 0, segs: 6, wander: 0.12,
        gravitropism: 0.0, droop: 0.0, tipCurl: 0.0, taper: 1.1,
      },
      {
        density: 0.65, whorl: 0, childStart: 0.2, childEnd: 0.95,
        angleBase: 1.25, angleTip: 0.85, lenRatio: 0.6, lenJitter: 0.4,
        radRatio: 0.55, segs: 4, wander: 0.32, gravitropism: 0.05,
        droop: 0.2, tipCurl: 0.3, taper: 1.15,
      },
      {
        density: 0.95, whorl: 0, childStart: 0.25, childEnd: 0.98,
        angleBase: 1.1, angleTip: 0.7, lenRatio: 0.48, lenJitter: 0.45,
        radRatio: 0.5, segs: 3, wander: 0.42, gravitropism: 0.03,
        droop: 0.18, tipCurl: 0.28, taper: 1.25,
      },
    ],
    foliage: {
      anchorLevel: 2, spacing: 0.5, tStart: 0.15, scale: [0.55, 0.9],
      tilt: 0.65, normalBend: 0.62, cardMode: 'cross', sizeK: 1.35,
    },
  },
];

/** Total baked (species, variant) pools. */
export const TREE_POOL_COUNT = TREE_SPECIES.length * STRUCTURAL_VARIANTS;
